// frontend/src/pages/Proposals/ProposalDetail.jsx
// Admin: review a vendor proposal — accept (creates Agreement) or reject
import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Download, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import {
  getProposal,
  acceptProposal,
  rejectProposal,
  getProposalTemplateUrl,
} from "../../api/proposal.api.js";
import { getEntities } from "../../api/entity.api.js";
import { Spinner, StatusBadge, ErrorMessage, formatDate } from "../../components/ui";

export default function ProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [panel, setPanel] = useState(null); // "accept" | "reject" | null
  const [acceptForm, setAcceptForm] = useState({
    title: "",
    scope: "",
    entityCacheId: "",
    startDate: "",
    endDate: "",
    objectOfAgreement: "",
    stakeholders: "",
    agreementType: "COLLABORATIVE",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState(null);

  const { data: proposal, isLoading } = useQuery({
    queryKey: ["proposal", id],
    queryFn: () => getProposal(id),
  });

  const { data: entities } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
    enabled: panel === "accept",
  });

  const setA = (k) => (e) => setAcceptForm((f) => ({ ...f, [k]: e.target.value }));

  const accept = useMutation({
    mutationFn: () => acceptProposal(id, acceptForm),
    onSuccess: (agreement) => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      navigate(`/agreements/${agreement.id}`);
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal menerima pengajuan"),
  });

  const reject = useMutation({
    mutationFn: () => rejectProposal(id, { rejectionReason: rejectReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      navigate("/proposals");
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal menolak pengajuan"),
  });

  const downloadTemplate = async () => {
    try {
      const { url } = await getProposalTemplateUrl(id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    }
  };

  if (isLoading) return <Spinner />;
  if (!proposal) return <p className="text-gray-500">Pengajuan tidak ditemukan.</p>;

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to="/proposals"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Kembali ke Daftar Pengajuan
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{proposal.title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {proposal.vendor?.companyName}
            {proposal.vendor?.picName && ` · ${proposal.vendor.picName}`}
            {" · "}
            {formatDate(proposal.createdAt)}
          </p>
        </div>
        <StatusBadge status={proposal.status} />
      </div>

      {/* Proposal detail */}
      <div className="card divide-y divide-gray-100 mb-5">
        <div className="px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Maksud & Tujuan
          </p>
          <p className="text-sm text-gray-900">{proposal.purpose}</p>
        </div>

        {proposal.description && (
          <div className="px-5 py-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
              Keterangan Tambahan
            </p>
            <p className="text-sm text-gray-900">{proposal.description}</p>
          </div>
        )}

        {proposal.ownTemplateFileName && (
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                Template Vendor
              </p>
              <p className="text-sm text-gray-900">{proposal.ownTemplateFileName}</p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
            >
              <Download size={13} /> Unduh
            </button>
          </div>
        )}

        {proposal.status === "REJECTED" && proposal.rejectionReason && (
          <div className="px-5 py-4 bg-red-50">
            <p className="text-xs text-red-600 uppercase tracking-wide font-medium mb-1">
              Alasan Penolakan
            </p>
            <p className="text-sm text-red-800">{proposal.rejectionReason}</p>
            {proposal.reviewedBy && (
              <p className="text-xs text-red-500 mt-1">oleh {proposal.reviewedBy.name}</p>
            )}
          </div>
        )}

        {proposal.createdAgreement && (
          <div className="px-5 py-4 flex items-center justify-between bg-green-50">
            <div>
              <p className="text-xs text-green-700 uppercase tracking-wide font-medium mb-1">
                Perjanjian Dibuat
              </p>
              <p className="text-sm text-green-900 font-medium">
                {proposal.createdAgreement.title}
              </p>
              <StatusBadge status={proposal.createdAgreement.status} />
            </div>
            <Link
              to={`/agreements/${proposal.createdAgreement.id}`}
              className="flex items-center gap-1.5 text-sm text-green-700 hover:underline"
            >
              <ExternalLink size={13} /> Lihat
            </Link>
          </div>
        )}
      </div>

      {/* Action buttons — only when PENDING */}
      {proposal.status === "PENDING" && !panel && (
        <div className="flex gap-3 mb-5">
          <button
            onClick={() => {
              setPanel("accept");
              setError(null);
              // Pre-fill title from proposal
              setAcceptForm((f) => ({ ...f, title: proposal.title }));
            }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
          >
            <CheckCircle size={16} />
            Terima & Buat Perjanjian
          </button>
          <button
            onClick={() => {
              setPanel("reject");
              setError(null);
            }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors text-sm"
          >
            <XCircle size={16} />
            Tolak Pengajuan
          </button>
        </div>
      )}

      {/* Accept panel */}
      {panel === "accept" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            accept.mutate();
          }}
          className="card p-6 space-y-4"
        >
          <h2 className="font-semibold text-gray-900">Buat Perjanjian dari Pengajuan Ini</h2>

          <div>
            <label className="label">
              Jenis Perjanjian <span className="text-red-500">*</span>
            </label>
            <select className="input" value={acceptForm.agreementType} onChange={setA("agreementType")}>
              <option value="COLLABORATIVE">Kolaboratif — pertukaran draft dengan vendor</option>
              <option value="INTERNAL">Internal — dokumentasi saja, tanpa persetujuan vendor</option>
            </select>
          </div>

          <div>
            <label className="label">
              Judul Perjanjian <span className="text-red-500">*</span>
            </label>
            <input
              className="input"
              value={acceptForm.title}
              onChange={setA("title")}
              placeholder="Judul resmi perjanjian"
              required
            />
          </div>

          <div>
            <label className="label">
              Ruang Lingkup <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input"
              rows={3}
              value={acceptForm.scope}
              onChange={setA("scope")}
              placeholder="Ruang lingkup perjanjian..."
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                Tanggal Mulai <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="input"
                value={acceptForm.startDate}
                onChange={setA("startDate")}
                required
              />
            </div>
            <div>
              <label className="label">
                Tanggal Berakhir <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="input"
                value={acceptForm.endDate}
                onChange={setA("endDate")}
                required
              />
            </div>
          </div>

          <div>
            <label className="label">Entitas Rhaya</label>
            <select className="input" value={acceptForm.entityCacheId} onChange={setA("entityCacheId")}>
              <option value="">— Pilih entitas (opsional) —</option>
              {entities?.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Objek Perjanjian</label>
            <input
              className="input"
              value={acceptForm.objectOfAgreement}
              onChange={setA("objectOfAgreement")}
              placeholder="Opsional"
            />
          </div>

          <div>
            <label className="label">Stakeholder</label>
            <input
              className="input"
              value={acceptForm.stakeholders}
              onChange={setA("stakeholders")}
              placeholder="Opsional"
            />
          </div>

          {error && <ErrorMessage error={error} />}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setPanel(null)}
              className="btn-secondary"
            >
              Batal
            </button>
            <button type="submit" disabled={accept.isPending} className="btn-primary">
              {accept.isPending ? "Membuat..." : "Buat Perjanjian"}
            </button>
          </div>
        </form>
      )}

      {/* Reject panel */}
      {panel === "reject" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            reject.mutate();
          }}
          className="card p-6 space-y-4 border-red-200"
        >
          <h2 className="font-semibold text-red-700">Tolak Pengajuan</h2>

          <div>
            <label className="label">
              Alasan Penolakan <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Jelaskan alasan penolakan pengajuan ini..."
              required
            />
          </div>

          {error && <ErrorMessage error={error} />}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setPanel(null)} className="btn-secondary">
              Batal
            </button>
            <button
              type="submit"
              disabled={reject.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
            >
              {reject.isPending ? "Menolak..." : "Konfirmasi Tolak"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
